import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  UserCheck, 
  ShoppingCart, 
  CreditCard, 
  AlertTriangle, 
  Copyright, 
  Eye, 
  Scale, 
  XOctagon, 
  MapPin, 
  RefreshCw, 
  Mail,
  ChevronLeft
} from 'lucide-react';

const TermsAndConditions = () => {
  // Updated to today's date
  const lastUpdated = "May 11, 2026";

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      
      {/* Back Button */}
      <div className="max-w-4xl mx-auto mb-6">
        <Link 
          to="/" 
          className="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors font-medium"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Home
        </Link>
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-3xl overflow-hidden border border-slate-100">
        
        {/* Beautiful Header Section */}
        <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white py-16 px-8 text-center relative overflow-hidden">
          {/* Decorative background circles */}
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-white opacity-5 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 rounded-full bg-white opacity-5 blur-2xl"></div>
          
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">Terms and Conditions</h1>
            <p className="text-emerald-100 text-lg font-medium">uCart Marketplace • Last Updated: {lastUpdated}</p>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8 md:p-12">
          <p className="text-slate-600 leading-relaxed text-lg mb-12">
            Welcome to <strong>uCart</strong>. These Terms and Conditions govern your access to and use of our platform. By registering an account or using our services, you agree to be bound by these rules. Please read them carefully.
          </p>

          <div className="space-y-12">
            
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Shield className="w-6 h-6" />
                </div>
                1. Acceptance of Terms
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                By accessing uCart (the "Platform"), you confirm that you have read, understood, and agreed to these Terms and Conditions. If you do not agree with any part of these terms, you are prohibited from using our services.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <UserCheck className="w-6 h-6" />
                </div>
                2. User Accounts & Registration
              </h2>
              <ul className="list-none space-y-3 text-slate-600 leading-relaxed pl-14">
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  Users must be at least 18 years of age, or have the consent of a legal guardian, to register and operate an account on uCart.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  You are strictly responsible for keeping your login credentials secure. Any activity occurring under your account is your responsibility.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  You agree to provide accurate, current, and complete information during the registration process.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <ShoppingCart className="w-6 h-6" />
                </div>
                3. Buying and Selling Policies
              </h2>
              <ul className="list-none space-y-3 text-slate-600 leading-relaxed pl-14">
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Accurate Listings:</strong> Sellers must ensure that all product descriptions, images, and prices are entirely accurate and truthful.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Verification Requirement:</strong> To ensure platform safety, sellers must complete a mandatory verification process by providing a valid Student ID or National Identification Number (NIN), accompanied by a live verification photo.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Buyer Responsibility:</strong> Buyers are expected to carefully review product details, conditions, and defects before initiating a purchase.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Marketplace Role:</strong> uCart acts solely as a digital marketplace to connect buyers and sellers. We do not directly own or manage the physical inventory listed on the platform.
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <CreditCard className="w-6 h-6" />
                </div>
                4. Payments and Fees
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                All financial transactions are processed securely through our trusted payment partner, Paystack. uCart may apply standard transaction or service fees to purchases, which will always be clearly displayed at checkout before you confirm payment.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                5. Prohibited Activities
              </h2>
              <p className="text-slate-600 mb-3 pl-14 font-medium">While using uCart, you strictly agree NOT to:</p>
              <ul className="list-none space-y-3 text-slate-600 leading-relaxed pl-14">
                <li className="relative before:content-['✗'] before:absolute before:-left-6 before:text-rose-500 before:font-bold">
                  List stolen goods, illegal substances, or items prohibited by university guidelines and Nigerian law.
                </li>
                <li className="relative before:content-['✗'] before:absolute before:-left-6 before:text-rose-500 before:font-bold">
                  Engage in scamming, fraudulent transactions, or identity theft.
                </li>
                <li className="relative before:content-['✗'] before:absolute before:-left-6 before:text-rose-500 before:font-bold">
                  Harass, abuse, threaten, or use hate speech against other users.
                </li>
                <li className="relative before:content-['✗'] before:absolute before:-left-6 before:text-rose-500 before:font-bold">
                  Attempt to hack, disrupt, or bypass the platform's security systems.
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Copyright className="w-6 h-6" />
                </div>
                6. Intellectual Property
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                All designs, logos, text, and graphics created by uCart are protected under intellectual property laws. You may not copy, reverse-engineer, or distribute our platform's content without explicit written permission.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Eye className="w-6 h-6" />
                </div>
                7. Privacy Policy
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                Your data protection is a top priority. Please review our <Link to="/privacy" className="text-emerald-600 font-semibold hover:underline">Privacy Policy</Link>, which details exactly how we collect, store, and utilize your personal information.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Scale className="w-6 h-6" />
                </div>
                8. Limitation of Liability
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                The uCart platform is provided on an "as is" basis. While we strive to maintain a secure environment, uCart shall not be held legally liable for any indirect, incidental, or consequential damages resulting from transactions or interactions between users.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <XOctagon className="w-6 h-6" />
                </div>
                9. Account Termination
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                uCart administration reserves the right to suspend, block, or permanently delete any user account at our sole discretion if we detect a violation of these Terms and Conditions.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <MapPin className="w-6 h-6" />
                </div>
                10. Governing Law
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                These Terms and Conditions shall be governed by and interpreted in accordance with the laws of the Federal Republic of Nigeria.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <RefreshCw className="w-6 h-6" />
                </div>
                11. Changes to Terms
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                We reserve the right to modify or replace these Terms at any time. Significant changes will be communicated via the platform. Your continued use of uCart after updates are posted constitutes your acceptance of the new Terms.
              </p>
            </section>

          </div>

          {/* Contact Footer Inside Card */}
          <div className="mt-16 pt-8 border-t border-slate-200 bg-slate-50 -mx-8 md:-mx-12 -mb-8 md:-mb-12 px-8 md:px-12 py-10">
            <h3 className="font-bold text-xl text-slate-800 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Contact Administration
            </h3>
            <p className="text-slate-600 mb-2">
              If you require clarification regarding any of these terms, please reach out to our support team:
            </p>
            <a href="mailto:hello.ibuyunn@gmail.com" className="text-emerald-600 font-bold text-lg hover:underline">
              hello.ibuyunn@gmail.com
            </a>
          </div>
        </div>
      </div>

      {/* Page Footer */}
      <div className="text-center mt-8 text-sm text-slate-500 font-medium">
        © {new Date().getFullYear()} uCart Marketplace. All Rights Reserved.
      </div>
    </div>
  );
};

export default TermsAndConditions;